import type {
  ParsedTemplateEntry,
  TemplateEntryDef,
  TemplateEntryField,
  TemplatesAddEntryInput,
  TemplatesUpdateEntryInput,
} from '@vivport/backend/types';
import { EMPTY_CONTROL_STATE, OTHER_OPTION, resolveControlValue, type FieldControlState } from './field-control-state';

export interface EntryFormState {
  category: string;
  title: FieldControlState;
  drafts: Record<string, FieldControlState>;
  photoIds: number[];
  notes: string;
}

export type AddEntryFormInput = Omit<TemplatesAddEntryInput, 'instanceId' | 'sectionCode'>;
export type UpdateEntryFormInput = Omit<TemplatesUpdateEntryInput, 'instanceId' | 'sectionCode' | 'entryNumber'>;

function controlStateFor(field: TemplateEntryField, value: string): FieldControlState {
  if (value.length === 0) return EMPTY_CONTROL_STATE;
  if (field.type === 'select' && !(field.options ?? []).includes(value)) {
    return { value: OTHER_OPTION, other: value };
  }
  return { value, other: '' };
}

function titleStateFor(items: string[], title: string): FieldControlState {
  if (title.length === 0 || items.length === 0) return { value: title, other: '' };
  return items.includes(title) ? { value: title, other: '' } : { value: OTHER_OPTION, other: title };
}

export function categoryItems(def: TemplateEntryDef, category: string): string[] {
  return def.categories?.find((candidate) => candidate.label === category)?.items ?? [];
}

export function initialEntryFormState(def: TemplateEntryDef, entry: ParsedTemplateEntry | null): EntryFormState {
  if (entry === null) {
    return { category: '', title: EMPTY_CONTROL_STATE, drafts: {}, photoIds: [], notes: '' };
  }
  const category = entry.category ?? '';
  const drafts: Record<string, FieldControlState> = {};
  for (const field of def.fields) {
    drafts[field.key] = controlStateFor(field, entry.values[field.key] ?? '');
  }
  return {
    category,
    title: titleStateFor(categoryItems(def, category), entry.title),
    drafts,
    photoIds: [],
    notes: entry.notes ?? '',
  };
}

export function resolveEntryTitle(state: EntryFormState): string {
  return resolveControlValue(state.title);
}

export function entryFormCanSubmit(def: TemplateEntryDef, state: EntryFormState): boolean {
  const categories = def.categories ?? [];
  const categoryChosen = categories.some((candidate) => candidate.label === state.category);
  return resolveEntryTitle(state).length > 0 && (categories.length === 0 || categoryChosen);
}

function resolvedValues(def: TemplateEntryDef, state: EntryFormState): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of def.fields) {
    values[field.key] = resolveControlValue(state.drafts[field.key] ?? EMPTY_CONTROL_STATE);
  }
  return values;
}

export function buildAddEntryInput(def: TemplateEntryDef, state: EntryFormState): AddEntryFormInput {
  const hasCategories = (def.categories?.length ?? 0) > 0;
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(resolvedValues(def, state))) {
    if (value.length > 0) values[key] = value;
  }
  const notes = state.notes.trim();
  return {
    title: resolveEntryTitle(state),
    ...(hasCategories ? { category: state.category } : {}),
    values,
    ...(def.notes && notes.length > 0 ? { notes } : {}),
    ...(def.photos && state.photoIds.length > 0 ? { photoIds: state.photoIds } : {}),
  };
}

// Only what differs from the saved entry is sent; a cleared field sends '' so the merge drops it.
// Null means nothing changed.
export function buildUpdateEntryInput(
  def: TemplateEntryDef,
  state: EntryFormState,
  entry: ParsedTemplateEntry
): UpdateEntryFormInput | null {
  const hasCategories = (def.categories?.length ?? 0) > 0;
  const title = resolveEntryTitle(state);
  const changedValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(resolvedValues(def, state))) {
    if (value !== (entry.values[key] ?? '')) changedValues[key] = value;
  }
  const notes = state.notes.trim();
  const input: UpdateEntryFormInput = {
    ...(title !== entry.title ? { title } : {}),
    ...(hasCategories && state.category !== (entry.category ?? '') ? { category: state.category } : {}),
    ...(Object.keys(changedValues).length > 0 ? { values: changedValues } : {}),
    ...(def.notes && notes !== (entry.notes ?? '') ? { notes } : {}),
    ...(def.photos && state.photoIds.length > 0 ? { addPhotoIds: state.photoIds } : {}),
  };
  return Object.keys(input).length === 0 ? null : input;
}
